import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Search, Trash2, Eye, ArrowLeft, Upload, Save, Users, FileText, LogOut,
} from "lucide-react";
import { api, getToken, clearToken } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { RichTextEditor } from "@/components/RichTextEditor";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS = ["draft", "published", "scheduled"];
const EMPTY = {
  title: "", slug: "", tag: "General", categories: "", tags: "", excerpt: "",
  body: "", cover_image: "", read_time: "5 min read", seo_title: "",
  meta_description: "", status: "draft", scheduled_at: "",
};

function toForm(a) {
  return {
    ...EMPTY, ...a,
    categories: (a.categories || []).join(", "),
    tags: (a.tags || []).join(", "),
    scheduled_at: a.scheduled_at ? a.scheduled_at.slice(0, 16) : "",
  };
}
function toPayload(f) {
  return {
    title: f.title, slug: f.slug || undefined, tag: f.tag,
    categories: f.categories.split(",").map((s) => s.trim()).filter(Boolean),
    tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
    excerpt: f.excerpt, body: f.body, cover_image: f.cover_image || null,
    read_time: f.read_time, seo_title: f.seo_title || null,
    meta_description: f.meta_description || null, status: f.status,
    scheduled_at: f.status === "scheduled" && f.scheduled_at
      ? new Date(f.scheduled_at).toISOString() : null,
  };
}

export default function AdminArticles() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null); // null = list view
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const autosaveRef = useRef(null);

  const guard = useCallback((err) => {
    if (err?.response?.status === 401) { clearToken(); navigate("/admin/login"); }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/articles", { params: { q: q || undefined, status: statusFilter || undefined } });
      setList(data.articles);
    } catch (e) { guard(e); }
  }, [q, statusFilter, guard]);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    if (editing === null) load();
  }, [load, editing, navigate]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setPreview(false); };
  const openEdit = (a) => { setForm(toForm(a)); setEditing(a.id); setPreview(false); };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useCallback(async (silent = false) => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editing === "new") {
        const { data } = await api.post("/admin/articles", toPayload(form));
        setEditing(data.id);
        setForm(toForm(data));
      } else {
        await api.put(`/admin/articles/${editing}`, toPayload(form));
      }
      if (!silent) { setSavedMsg("Saved"); setTimeout(() => setSavedMsg(""), 2000); }
    } catch (e) { guard(e); } finally { setSaving(false); }
  }, [form, editing, guard]);

  // Autosave existing article 2.5s after last edit
  useEffect(() => {
    if (editing && editing !== "new") {
      clearTimeout(autosaveRef.current);
      autosaveRef.current = setTimeout(() => save(true), 2500);
      return () => clearTimeout(autosaveRef.current);
    }
  }, [form, editing, save]);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: `${BACKEND}${data.url}` }));
    } catch (err) { guard(err); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/articles/${id}`); load(); } catch (e) { guard(e); }
  };

  const logout = () => { clearToken(); navigate("/admin/login"); };

  // ---------- LIST VIEW ----------
  if (editing === null) {
    return (
      <div className="min-h-screen bg-black" data-testid="admin-articles">
        <TopBar navigate={navigate} logout={logout} active="articles" />
        <div className="max-w-[1280px] mx-auto px-6 py-10">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-display text-4xl text-white">Articles</h1>
            <button onClick={openNew} data-testid="new-article-btn" className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium px-5 py-3 transition-colors">
              <Plus size={16} /> New Article
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search titles…" data-testid="articles-search"
                className="w-full bg-black border border-white/20 text-white pl-11 pr-4 py-3 font-body focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="articles-status-filter"
              className="bg-black border border-white/20 text-white px-4 py-3 font-body focus:outline-none focus:border-[#D4AF37]">
              <option value="">All statuses</option>
              {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="border border-white/12">
            {list.length === 0 ? (
              <p className="text-white/40 font-body text-center py-12">No articles yet.</p>
            ) : list.map((a) => (
              <div key={a.id} data-testid={`article-row-${a.id}`} className="flex items-center justify-between px-5 py-4 border-b border-white/6 hover:bg-white/[0.03]">
                <div className="min-w-0">
                  <p className="font-body text-white/90 truncate">{a.title}</p>
                  <p className="font-body text-xs text-white/40 mt-1">{a.tag} · {a.read_time}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-1 border ${
                    a.status === "published" ? "border-[#D4AF37]/50 text-[#D4AF37]" :
                    a.status === "scheduled" ? "border-blue-400/40 text-blue-300" : "border-white/20 text-white/50"}`}>
                    {a.status}
                  </span>
                  <button onClick={() => openEdit(a)} className="text-[#D4AF37] text-sm font-body hover:underline">Edit</button>
                  <button onClick={() => remove(a.id)} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------- EDITOR VIEW ----------
  return (
    <div className="min-h-screen bg-black" data-testid="article-editor">
      <TopBar navigate={navigate} logout={logout} active="articles" />
      <div className="max-w-[1100px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 text-white/60 hover:text-white font-body text-sm">
            <ArrowLeft size={16} /> All Articles
          </button>
          <div className="flex items-center gap-3">
            {savedMsg && <span className="text-[#D4AF37] text-sm font-body">{savedMsg}</span>}
            <button onClick={() => setPreview((p) => !p)} data-testid="preview-toggle" className="inline-flex items-center gap-2 border border-white/20 text-white/80 hover:border-[#D4AF37] px-4 py-2.5 font-body text-sm">
              <Eye size={15} /> {preview ? "Edit" : "Preview"}
            </button>
            <button onClick={() => save(false)} disabled={saving} data-testid="save-article" className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium px-5 py-2.5 disabled:opacity-60">
              <Save size={15} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {preview ? (
          <article className="bg-white text-black p-10 lg:p-16">
            {form.cover_image && <img src={form.cover_image} alt="" className="w-full h-72 object-cover mb-8" />}
            <p className="font-body text-xs tracking-[0.2em] uppercase text-[#B8952E]">{form.tag}</p>
            <h1 className="font-display text-4xl lg:text-5xl mt-4">{form.title || "Untitled"}</h1>
            <p className="font-body text-black/50 mt-4">{form.excerpt}</p>
            <div className="prose max-w-none mt-8 rte-preview" dangerouslySetInnerHTML={{ __html: form.body }} />
          </article>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main */}
            <div className="lg:col-span-2 space-y-5">
              <Field label="Title">
                <input value={form.title} onChange={set("title")} data-testid="article-title"
                  className="w-full bg-black border border-white/20 text-white text-xl font-display px-4 py-3 focus:outline-none focus:border-[#D4AF37]" placeholder="Article title" />
              </Field>
              <Field label="Excerpt">
                <textarea value={form.excerpt} onChange={set("excerpt")} rows={2} data-testid="article-excerpt"
                  className="w-full bg-black border border-white/20 text-white px-4 py-3 font-body resize-none focus:outline-none focus:border-[#D4AF37]" />
              </Field>
              <Field label="Body">
                <RichTextEditor value={form.body} onChange={(html) => setForm((f) => ({ ...f, body: html }))} testId="article-body" />
              </Field>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              <SideCard title="Publishing">
                <Field label="Status">
                  <select value={form.status} onChange={set("status")} data-testid="article-status"
                    className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body focus:outline-none focus:border-[#D4AF37]">
                    {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                {form.status === "scheduled" && (
                  <Field label="Publish at">
                    <input type="datetime-local" value={form.scheduled_at} onChange={set("scheduled_at")} data-testid="article-schedule"
                      className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body focus:outline-none focus:border-[#D4AF37]" />
                  </Field>
                )}
                <Field label="Read time">
                  <input value={form.read_time} onChange={set("read_time")}
                    className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body focus:outline-none focus:border-[#D4AF37]" />
                </Field>
              </SideCard>

              <SideCard title="Featured Image">
                {form.cover_image && <img src={form.cover_image} alt="" className="w-full h-32 object-cover mb-3" />}
                <label className="inline-flex items-center gap-2 border border-white/20 text-white/80 hover:border-[#D4AF37] px-4 py-2.5 font-body text-sm cursor-pointer">
                  <Upload size={15} /> Upload
                  <input type="file" accept="image/*" onChange={uploadImage} data-testid="article-image-upload" className="hidden" />
                </label>
                <input value={form.cover_image} onChange={set("cover_image")} placeholder="…or paste image URL"
                  className="w-full bg-black border border-white/20 text-white px-3 py-2 font-body text-sm mt-3 focus:outline-none focus:border-[#D4AF37]" />
              </SideCard>

              <SideCard title="Organization">
                <Field label="Primary Category">
                  <input value={form.tag} onChange={set("tag")} data-testid="article-tag"
                    className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body focus:outline-none focus:border-[#D4AF37]" />
                </Field>
                <Field label="Categories (comma-separated)">
                  <input value={form.categories} onChange={set("categories")}
                    className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body text-sm focus:outline-none focus:border-[#D4AF37]" />
                </Field>
                <Field label="Tags (comma-separated)">
                  <input value={form.tags} onChange={set("tags")}
                    className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body text-sm focus:outline-none focus:border-[#D4AF37]" />
                </Field>
                <Field label="URL Slug">
                  <input value={form.slug} onChange={set("slug")} placeholder="auto from title" data-testid="article-slug"
                    className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body text-sm focus:outline-none focus:border-[#D4AF37]" />
                </Field>
              </SideCard>

              <SideCard title="SEO">
                <Field label="SEO Title">
                  <input value={form.seo_title} onChange={set("seo_title")}
                    className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body text-sm focus:outline-none focus:border-[#D4AF37]" />
                </Field>
                <Field label="Meta Description">
                  <textarea value={form.meta_description} onChange={set("meta_description")} rows={3}
                    className="w-full bg-black border border-white/20 text-white px-3 py-2.5 font-body text-sm resize-none focus:outline-none focus:border-[#D4AF37]" />
                </Field>
              </SideCard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-body text-xs tracking-[0.16em] uppercase text-white/45">{label}</label>
      {children}
    </div>
  );
}
function SideCard({ title, children }) {
  return (
    <div className="border border-white/12 p-5 space-y-4">
      <p className="font-body text-xs tracking-[0.2em] uppercase text-[#D4AF37]">{title}</p>
      {children}
    </div>
  );
}
export function TopBar({ navigate, logout, active }) {
  return (
    <div className="border-b border-white/10 sticky top-0 bg-black/90 backdrop-blur-xl z-30">
      <div className="max-w-[1280px] mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <img src={IMAGES.logo} alt="E2E" className="h-9" />
          <nav className="flex items-center gap-1">
            <button onClick={() => navigate("/admin")} className={`inline-flex items-center gap-2 px-4 py-2 font-body text-sm ${active === "contacts" ? "text-[#D4AF37]" : "text-white/60 hover:text-white"}`}>
              <Users size={15} /> CRM
            </button>
            <button onClick={() => navigate("/admin/articles")} className={`inline-flex items-center gap-2 px-4 py-2 font-body text-sm ${active === "articles" ? "text-[#D4AF37]" : "text-white/60 hover:text-white"}`}>
              <FileText size={15} /> Articles
            </button>
          </nav>
        </div>
        <button onClick={logout} className="inline-flex items-center gap-2 text-white/60 hover:text-white px-3 py-2 font-body text-sm">
          <LogOut size={15} /> Logout
        </button>
      </div>
    </div>
  );
}
